import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { useTranslation } from 'react-i18next';
import { pavoApiService, type PavoDevice } from '../../../services/pavoApiService';

interface PavoP2pInlineSelectorProps {
  companyId?: string;
  branchId?: string;
  /** Seçili cihazın `deviceCode` değeri. */
  value?: string;
  /** Yeni cihaz seçildiğinde tetiklenir; kullanıcı temizlerse boş string döner. */
  onChange: (deviceCode: string) => void;
}

/**
 * KioskFiscalPrinterTab içinde "PavoPay > REST-P2P" alt-tipi seçildiğinde
 * gösterilen inline cihaz seçici. Sadece `pavo_rest_p2p` modundaki Pavo
 * cihazlarını listeler. Mevcut `PavoP2PDeviceSelectDialog` kart-modal
 * pattern'inin form-friendly Select varyantıdır; ayar paneline gömülü
 * kalır, dialog açmaz.
 */
const PavoP2pInlineSelector: React.FC<PavoP2pInlineSelectorProps> = ({
  companyId,
  branchId,
  value,
  onChange,
}) => {
  const { t } = useTranslation('salesSystem');
  const [devices, setDevices] = useState<PavoDevice[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDevices = useCallback(async () => {
    if (!companyId) {
      setDevices([]);
      return;
    }
    setLoading(true);
    try {
      const all = await pavoApiService.getPavoDevices(companyId, branchId);
      const filtered = all.filter((d) => d.deviceMode === 'pavo_rest_p2p');
      filtered.sort((a, b) => (a.pavoNumber ?? 999) - (b.pavoNumber ?? 999));
      setDevices(filtered);
    } catch (e) {
      console.error('[PavoP2pInlineSelector] Cihazlar yüklenemedi:', e);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  // Seçili cihaz hâlâ listede yok (örneğin silinmiş ya da farklı şubeye taşınmış)
  // ise kullanıcıyı uyarmak için stale state'i tespit ediyoruz.
  const selectedExists = !value || devices.some((d) => d.deviceCode === value);
  const selectedDevice = devices.find((d) => d.deviceCode === value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="pavo-p2p-device-label">
            {t('kioskFiscalTab.pavopay.integrationMode.deviceLabel')}
          </InputLabel>
          <Select
            labelId="pavo-p2p-device-label"
            id="pavo-p2p-device"
            value={selectedExists ? (value || '') : ''}
            label={t('kioskFiscalTab.pavopay.integrationMode.deviceLabel')}
            onChange={handleChange}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    {t('kioskFiscalTab.pavopay.integrationMode.devicePlaceholder')}
                  </Typography>
                );
              }
              const dev = devices.find((d) => d.deviceCode === selected);
              if (!dev) return selected;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PointOfSaleIcon fontSize="small" sx={{ color: '#7c4dff' }} />
                  <Typography variant="body2" component="span">
                    Pavo #{dev.pavoNumber ?? '?'} — {dev.deviceName}
                  </Typography>
                  {dev.isOnline && (
                    <Chip
                      icon={<FiberManualRecordIcon sx={{ fontSize: 8 }} />}
                      label="Online"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              );
            }}
          >
            {devices.length === 0 && !loading && (
              <MenuItem value="" disabled>
                <Typography variant="body2" color="text.secondary">
                  {t('kioskFiscalTab.pavopay.integrationMode.noDevices')}
                </Typography>
              </MenuItem>
            )}
            {devices.map((d) => (
              <MenuItem key={d.deviceCode} value={d.deviceCode}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#7c4dff',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    #{d.pavoNumber ?? '?'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {d.deviceName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {d.deviceCode}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<FiberManualRecordIcon sx={{ fontSize: 8 }} />}
                    label={d.isOnline ? 'Online' : 'Offline'}
                    size="small"
                    color={d.isOnline ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title={t('kioskFiscalTab.pavopay.integrationMode.refresh')}>
          <span>
            <IconButton onClick={loadDevices} disabled={loading || !companyId} size="large">
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!companyId && (
        <Alert severity="warning" variant="outlined">
          {t('kioskFiscalTab.pavopay.integrationMode.missingCompany')}
        </Alert>
      )}

      {companyId && !loading && devices.length === 0 && (
        <Alert severity="warning" variant="outlined">
          {t('kioskFiscalTab.pavopay.integrationMode.noDevicesHint')}
        </Alert>
      )}

      {value && !selectedExists && (
        <Alert severity="error" variant="outlined">
          {t('kioskFiscalTab.pavopay.integrationMode.staleDevice', { code: value })}
        </Alert>
      )}

      {selectedDevice && !selectedDevice.isOnline && (
        <Alert severity="info" variant="outlined">
          {t('kioskFiscalTab.pavopay.integrationMode.offlineSelectedHint')}
        </Alert>
      )}
    </Box>
  );
};

export default PavoP2pInlineSelector;
